import {
  InputAdornment,
  TextField as MuiTextField,
  TextFieldProps,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { ReactElement, useState } from "react";
import {
  FieldPathByValue,
  FieldValues,
  useController,
  UseControllerProps,
} from "react-hook-form";

import { FlexBox } from "~frontend/components/FlexBox";
import theme from "~frontend/theme";

// Converts a stored decimal (e.g. 0.25) to the integer display value (e.g. 25).
function formatStoredAsInt(value?: number | null): string {
  return value ? String(Math.round(value * 100)) : "";
}

export function RhfPercentageField<
  T extends FieldValues,
  TName extends FieldPathByValue<T, number | null | undefined>,
>(
  props: Omit<TextFieldProps, "value" | "onChange" | "name"> &
    UseControllerProps<T, TName> & {
      optional?: boolean;
      bottomLabel?: string;
    }
): ReactElement {
  const { field, fieldState } = useController(props);
  const { optional, bottomLabel, ...rest } = props;

  const [displayValue, setDisplayValue] = useState(() =>
    formatStoredAsInt(field.value)
  );

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const inputValue = event.target.value;
    setDisplayValue(inputValue);

    if (inputValue === "") {
      field.onChange("");
      return;
    }

    const numericValue = parseFloat(inputValue);
    if (isNaN(numericValue)) {
      field.onChange(inputValue);
    } else {
      field.onChange(numericValue / 100);
    }
  }

  type InputLabelLike = { sx?: SxProps<Theme>; [key: string]: unknown };
  const externalInputLabel = (props.InputLabelProps ??
    props.slotProps?.inputLabel) as InputLabelLike | undefined;
  const { sx: externalInputLabelSx, ...externalInputLabelRest } =
    externalInputLabel ?? {};

  return (
    <MuiTextField
      type="number"
      onChange={handleChange}
      onBlur={field.onBlur}
      value={displayValue}
      name={field.name}
      inputRef={field.ref}
      error={Boolean(fieldState.error)}
      helperText={!fieldState.error ? bottomLabel : fieldState.error.message}
      {...(rest as TextFieldProps)}
      label={
        <FlexBox alignItems="center" gap={1}>
          {props.label}
          {optional ? <Typography> (optional)</Typography> : ""}
        </FlexBox>
      }
      slotProps={{
        inputLabel: {
          sx: [
            { width: "133%", maxWidth: `calc(140% - ${theme.spacing(4)})` },
            ...(externalInputLabelSx != null ? [externalInputLabelSx] : []),
          ] as SxProps<Theme>,
          shrink: true,
          ...externalInputLabelRest,
        },
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <Typography display="inline" variant="body1Bold">
                %
              </Typography>
            </InputAdornment>
          ),
          ...(props.InputProps || props.slotProps?.input || {}),
        },
      }}
    />
  );
}
